import { useTranslation } from "react-i18next";
import { useMemo } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { Me } from "@arc/contracts";
import type { AuthSession } from "../../auth/keycloak.js";
import type { TenantBootstrap } from "../../tenant/bootstrap.js";
import { useFacilities } from "../facilities/useFacilities.js";
import { useFacilityAuthz } from "../facilities/useFacilityAuthz.js";
import { useFacilityNavParams } from "../facilities/drilldown/useFacilityNavParams.js";
import type { WorkspaceView as WorkspacePage } from "../facilities/drilldown/nav-state.js";
import { canSee, entryFor, visibleGroups, type WorkspaceCaps } from "./workspace-pages.js";
import { WorkspaceMobileNav, WorkspaceSidebar } from "./WorkspaceSidebar.js";
import { ratingsEnabledFor } from "@arc/tenant-config";
import { OverviewPage } from "../dashboard/OverviewPage.js";
import { ManualListPage } from "../dashboard/manual-list/ManualListPage.js";
import { TasksPage } from "../dashboard/tasks/TasksPage.js";
import { HistoryPage } from "../dashboard/history/HistoryPage.js";
import { FavouriteFacilitiesPage } from "../dashboard/favourite-facilities/FavouriteFacilitiesPage.js";
import { ProfilePage } from "./pages/ProfilePage.js";
import { QrLabelsPage } from "./pages/QrLabelsPage.js";
import { RolesView } from "../roles/RolesView.js";
import { SensorsView } from "../sensors/SensorsView.js";
import { KioskProfilesView } from "../kiosk/KioskProfilesView.js";
import { RolesForbiddenError, useRoles } from "../roles/useRoles.js";
import { ComingSoonPage } from "./pages/ComingSoonPage.js";
import { NotAvailablePage } from "./pages/NotAvailablePage.js";

/**
 * The "Dashboard & settings" workspace (ADR-032) — the legacy `GeneralOptions` view: a sidebar of grouped pages and one
 * content pane, rendered by `App.tsx` ABOVE the realm split whenever `arc_view` is set (and no editor is open). Owns the
 * page `<h1>`. NOT a modal: the header stays live (its toggle closes this).
 *
 * Gating mirrors the legacy sidebar from the caller's facility tree (the server stays authoritative on every page's
 * data): facility-management rights = bypass or `manage` on any node; access-management rights = the bypass hint (a role
 * with HPFP `access_manage`); SuperAdmin has no client signal yet, so its two pages stay hidden.
 */
export function WorkspaceView({ session, me, tenant, view }: { session: AuthSession; me: Me; tenant: TenantBootstrap; view: WorkspacePage }) {
  const { t } = useTranslation();
  const nav = useFacilityNavParams();
  const facilities = useFacilities(session);
  const flat = useMemo(() => facilities.data ?? [], [facilities.data]);
  const authz = useFacilityAuthz(flat);
  // `canManageRoles` is the SERVER's verdict (`GET /roles` admits the caller or 403s), never the client bypass hint: the
  // engine reads a NULL HPFP as uncapped, so a role with no level but an access_manage GRANT would read as bypass here
  // while the role gate (the stored manage prefix) refuses it (diff panel). `isSuper` comes with the same read.
  const roles = useRoles(session);
  const caps = useMemo<WorkspaceCaps>(
    () => ({
      canManageFacilities: authz.bypass || authz.canAddIds.size > 0,
      canManageRoles: roles.isSuccess,
      isSuper: roles.data?.caller.isSuper ?? false,
      ratingsEnabled: ratingsEnabledFor(tenant.realm), // config-as-code, known synchronously (ADR-034 D6)
    }),
    [authz.bypass, authz.canAddIds, roles.isSuccess, roles.data, tenant.realm],
  );
  // Hold the Settings group until the tree has answered — a group that appears a beat later is a flash, one that
  // disappears is worse. The tree is normally already cached by the navigator.
  const settled = !facilities.isPending && !roles.isPending;
  const groups = useMemo(() => visibleGroups(settled ? caps : { canManageFacilities: false, canManageRoles: false, isSuper: false, ratingsEnabled: caps.ratingsEnabled }), [caps, settled]);
  const entry = entryFor(view);
  // A read that FAILED is not a permission verdict: the caller gets a retry, never a false "not available" (diff
  // panels — the facilities tree, and the roles read for the pages that depend on it; a 403 on the roles read IS the
  // verdict). A gated page's verdict waits for both reads (an eager denial would flash on every uncached deep link).
  const rolesFailed = roles.isError && !(roles.error instanceof RolesForbiddenError);
  const dependsOnRoles = entry.requires === "roles" || entry.requires === "super";
  const allowed: boolean | "pending" | "error" =
    entry.requires === "none" || entry.requires === "ratings" ? canSee(entry, caps) : !settled ? "pending" : facilities.isError || (dependsOnRoles && rolesFailed) ? "error" : canSee(entry, caps);
  const retry = () => {
    if (facilities.isError) void facilities.refetch();
    if (rolesFailed) void roles.refetch();
  };

  return (
    <div className="flex min-h-full flex-1 max-md:flex-col">
      <WorkspaceSidebar groups={groups} current={view} onSelect={(v) => nav.openView(v, true)} onBack={nav.closeView} />
      <WorkspaceMobileNav groups={groups} current={view} onSelect={(v) => nav.openView(v, true)} onBack={nav.closeView} />
      {/* The overview's cards sit on the grey band, as the editor's and the selections' do (design pass 2026-11-04). */}
      <section aria-labelledby="workspace-heading" className={cn("min-w-0 flex-1 overflow-y-auto p-4 lg:p-6", view === "overview" && "bg-muted")}>
        {/* The roles page places the heading itself — at the top of its tree column, level with the selected role's
            name (owner: the two panes' titles on one row); every other page gets it here. */}
        {allowed === true && entry.status === "ready" && (view === "roles" || view === "sensors" || view === "generic-user") ? null : (
          <h1 id="workspace-heading" className="mb-4 text-xl font-semibold">
            {t(entry.labelKey)}
          </h1>
        )}
        {allowed === "pending" ? (
          <p role="status" className="text-muted-foreground text-sm">
            {t("workspace.loadingEllipsis")}
          </p>
        ) : allowed === "error" ? (
          <div className="flex flex-col items-start gap-2">
            <p role="alert" className="text-destructive text-sm">
              {facilities.isError ? t("workspace.couldntCheckFacilities") : t("workspace.couldntCheckRoles")}
            </p>
            <Button variant="outline" size="sm" onPress={retry}>
              {t("workspace.tryAgain")}
            </Button>
          </div>
        ) : !allowed ? (
          <NotAvailablePage entry={entry} />
        ) : entry.status === "soon" ? (
          <ComingSoonPage entry={entry} />
        ) : view === "overview" ? (
          <OverviewPage session={session} me={me} />
        ) : view === "profile" ? (
          <ProfilePage me={me} tenant={tenant} />
        ) : view === "manuals" || view === "favourites" || view === "ratings" ? (
          <ManualListPage session={session} mode={view} />
        ) : view === "tasks" ? (
          <TasksPage session={session} />
        ) : view === "history" ? (
          <HistoryPage session={session} />
        ) : view === "favourite-facilities" ? (
          <FavouriteFacilitiesPage session={session} />
        ) : view === "qr" ? (
          <QrLabelsPage session={session} />
        ) : view === "generic-user" ? (
          <KioskProfilesView
            session={session}
            heading={
              <h1 id="workspace-heading" className="text-xl font-semibold">
                {t(entry.labelKey)}
              </h1>
            }
          />
        ) : view === "sensors" ? (
          <SensorsView
            session={session}
            heading={
              <h1 id="workspace-heading" className="text-xl font-semibold">
                {t(entry.labelKey)}
              </h1>
            }
          />
        ) : view === "roles" ? (
          <RolesView
            session={session}
            heading={
              <h1 id="workspace-heading" className="text-xl font-semibold">
                {t(entry.labelKey)}
              </h1>
            }
          />
        ) : (
          <ComingSoonPage entry={entry} />
        )}
      </section>
    </div>
  );
}
